import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from "react";

import { Tooltip, TooltipContent, TooltipTrigger } from "../../components/ui/tooltip";
import { cn } from "../../lib/utils";

export type FabVariant = "amber" | "purple" | "navy";

type FabButtonProperties = {
  /** Accessible label for the button — also shown as tooltip text. */
  label: string;
  /** Icon to render inside the FAB. Should be a Lucide icon or equivalent. */
  icon: ReactNode;
  /** Visual accent variant mapped to uikit design tokens. */
  variant: FabVariant;
  /** Whether the associated panel is currently open. */
  expanded: boolean;
  /** ID of the panel this FAB controls — used for aria-controls. */
  controls: string;
  /** The type of panel: "dialog" for drawer, "menu" for popover/menu. */
  hasPopup: "dialog" | "menu";
  /** Click handler to toggle the panel. */
  onClick: () => void;
  /** Disables the FAB (e.g. read-only mode) — blocks click and dims the button. */
  disabled?: boolean;
  /** Optional additional class names. */
  className?: string;
};

const variantClasses: Record<FabVariant, string> = {
  // amber: dark icon on amber (#f59e0b) — white would fail WCAG SC 1.4.11 at 2.15:1
  amber: "text-fab-navy bg-fab-amber hover:bg-fab-amber-hover focus-visible:ring-fab-amber",
  purple: "text-white bg-fab-purple hover:bg-fab-purple-hover focus-visible:ring-fab-purple",
  navy: "text-white bg-fab-navy hover:bg-fab-navy-hover focus-visible:ring-fab-navy",
};

/**
 * Circular floating-action-button primitive with a hover tooltip and full ARIA wiring.
 * Used by AnalystNotesPopover, InternalAuditDrawer, and ActionsPopover to create the
 * fixed-position cluster in the review page.
 */
const FabButton = forwardRef<HTMLButtonElement, FabButtonProperties & ButtonHTMLAttributes<HTMLButtonElement>>(
  function FabButton(
    { label, icon, variant, expanded, controls, hasPopup, onClick, disabled, className, ...rest },
    forwardedReference
  ) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            ref={forwardedReference}
            type="button"
            // Spread Radix-injected props (ref anchor, data-state, etc.) first so
            // our semantic ARIA attributes and the cluster onClick always win.
            // The Popover is fully controlled (open/onOpenChange), so Radix's
            // injected onClick only duplicates what our onClick already does.
            {...rest}
            aria-label={label}
            aria-expanded={expanded}
            aria-controls={controls}
            aria-haspopup={hasPopup}
            disabled={disabled}
            onClick={onClick}
            className={cn(
              "flex h-12 w-12 items-center justify-center rounded-full shadow-lg transition-all duration-200",
              "hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
              "disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none",
              variantClasses[variant],
              className
            )}
          >
            <span aria-hidden="true" className="flex items-center justify-center">
              {icon}
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">{label}</TooltipContent>
      </Tooltip>
    );
  }
);

FabButton.displayName = "FabButton";

export default FabButton;
